import { i18n } from '../i18n/i18n';

import { isApiError } from './errors';

/**
 * Where the error texts of a backend module live when not at `<module>:errors.*` (ARCHITECTURE §1): modules whose
 * screens belong to a frontend module with another id (`identity.locked_out` → `company:errors.locked_out`) and
 * platform modules without screens of their own (`files.no_file` → `shared:fileErrors.no_file`).
 */
const ERROR_KEY_PREFIXES: Readonly<Record<string, string>> = {
  identity: 'company:errors',
  files: 'shared:fileErrors',
};

/**
 * Human-readable, localized message for any error thrown by the API layer.
 *
 * Server error codes follow `<module>.<error>` (e.g. `tasks.not_found`). When the module has a translation at
 * `<module>:errors.<error>` (or its alias, see above) it is used; otherwise a translated message for the HTTP status.
 * The ProblemDetails `detail` is never shown: it is English text meant for developers.
 */
export function getErrorMessage(error: unknown): string {
  const t = i18n.t.bind(i18n);
  if (isApiError(error)) {
    if (error.isNetworkError) return t('shared:errors.network');
    if (error.isAborted) return t('shared:errors.aborted');

    const dot = error.code.indexOf('.');
    if (dot > 0) {
      const module = error.code.slice(0, dot);
      const key = `${ERROR_KEY_PREFIXES[module] ?? `${module}:errors`}.${error.code.slice(dot + 1)}`;
      if (i18n.exists(key)) return t(key);
    }
    if (error.status === 401) return t('shared:errors.unauthorized');
    if (error.status === 403) return t('shared:errors.forbidden');
    if (error.status === 404) return t('shared:errors.notFound');
    if (error.status === 409) return t('shared:errors.conflict');
    if (error.status === 413) return t('shared:errors.tooLarge');
    if (error.status === 410) return t('shared:errors.gone');
    if (error.status === 429) return t('shared:errors.rateLimited');
    if (error.status >= 500) return t('shared:errors.server');
    if (error.isValidation) return t('shared:errors.validation');
    if (error.status >= 400) return t('shared:errors.badRequest');
  }
  return t('shared:errors.unknown');
}
